/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import '../styles/components.css';
import { useAuth } from '../contexts/AuthContext';

const NGOSignup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const cname = searchParams.get('cname');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState<any>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [showRoleConfirmation, setShowRoleConfirmation] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [ngoId, setNgoId] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const hashStr = window.location.hash.substring(1);

      // Case 1: User arrived via Supabase email invite (hash has access_token)
      if (hashStr.includes('access_token=')) {
        navigate('/ngo/accept-invite' + window.location.search + window.location.hash, { replace: true });
        return;
      }

      // Case 2: User has a session already
      if (session) {
        const { data: ngoLink } = await supabase
          .from('ngo_users')
          .select('ngo_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (ngoLink?.ngo_id) {
          // Already fully set up - go to dashboard
          navigate(`/ngo/ngo/dashboard`, { replace: true });
          return;
        }

        // Has session but no ngo link - redirect to accept-invite to complete setup
        navigate('/ngo/accept-invite' + window.location.search, { replace: true });
        return;
      }

      // Case 3: No session, no hash - token-based flow (manual link)
      if (token) {
        loadInvitation();
      } else {
        setError('Invalid invitation link. Please check your email.');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadInvitation = async () => {
    if (!token) return;

    // Strategy 1: Try the secure RPC first
    try {
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('verify_ngo_invitation', { token });

      if (rpcData && rpcData.valid) {
        if (rpcData.status !== 'pending' && rpcData.status !== 'accepted') {
          setError('This invitation is no longer valid.');
          return;
        }
        if (rpcData.expires_at && new Date(rpcData.expires_at) < new Date()) {
          setError('This invitation has expired.');
          return;
        }
        setInvitation(rpcData);
        // Company name comes from RPC
        if (rpcData.companies?.name) {
          setCompanyName(rpcData.companies.name);
        } else if (rpcData.company_name) {
          setCompanyName(rpcData.company_name);
        }
        return; // Success - done
      }

      if (rpcError) {
        console.warn('verify_ngo_invitation RPC failed:', rpcError.message);
      }
    } catch (e) {
      console.warn('verify_ngo_invitation RPC threw exception:', e);
    }

    // Strategy 2: Direct query fallback (works if ngo_invitations has a permissive read policy)
    console.warn('Falling back to direct ngo_invitations query...');
    const { data: directData, error: directError } = await supabase
      .from('ngo_invitations')
      .select('*')
      .eq('invitation_token', token)
      .maybeSingle();

    if (directError) {
      console.error('Direct invitation query failed:', directError.message);
      setError('Unable to load invitation. Please try again or contact support.');
      return;
    }

    if (!directData) {
      setError('Invalid or expired invitation link. Please request a new invitation.');
      return;
    }

    if (directData.status !== 'pending' && directData.status !== 'accepted') {
      setError('This invitation is no longer valid.');
      return;
    }

    if (directData.expires_at && new Date(directData.expires_at) < new Date()) {
      setError('This invitation has expired.');
      return;
    }

    setInvitation(directData);

    // Fetch company name separately (no join needed)
    if (directData.company_id) {
      if (cname) {
        setCompanyName(cname);
        return;
      }
      const { data: companyData } = await supabase
        .from('companies')
        .select('name')
        .eq('id', directData.company_id)
        .maybeSingle();
      if (companyData) {
        setCompanyName(companyData.name);
      }
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      if (!invitation || !token) throw new Error('Invalid invitation');

      // 1. Sign up/in the user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.ngo_email,
        password: formData.password
      });

      let finalUser = authData.user;

      // Check if Supabase returned a fake user (email already exists and enumeration protection is on)
      const isFakeUser = finalUser && finalUser.identities && finalUser.identities.length === 0;

      if (signUpError || !finalUser || isFakeUser) {
        if (isFakeUser || signUpError?.message?.includes('already registered') || signUpError?.status === 400) {
          console.log('Account may already exist, attempting sign-in...');
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: invitation.ngo_email,
            password: formData.password
          });
          
          if (signInError) {
            if (signInError.message.includes('Invalid login credentials')) {
              throw new Error('An account with this email already exists. Please use your existing password. If you forgot it, go to the login page to reset it.');
            }
            throw new Error('Password mismatch or account issue. Please try again.');
          }
          finalUser = signInData.user;
        } else {
          throw signUpError || new Error('Signup failed');
        }
      }

      if (!finalUser) throw new Error('Failed to establish session');

      // 2. Atomic Setup via Backend RPC
      // Database synchronization can take time, so we retry with increasing delays
      let setupResult = null;
      let setupError = null;

      console.log(`Starting account setup for user: ${finalUser.id}`);

      // Initial wait to let triggers finish
      await new Promise(resolve => setTimeout(resolve, 2000));

      for (let attempt = 0; attempt < 5; attempt++) {
        console.log(`Setup attempt ${attempt + 1}...`);

        const { data, error } = await supabase.rpc('setup_ngo_account', {
          user_id_param: finalUser.id,
          invitation_token_param: token,
          email_param: invitation.ngo_email
        });

        if (!error && data?.success) {
          setupResult = data;
          console.log('Setup successful!');
          break;
        }

        const errObj = error || (data ? new Error(data.message || data.error) : new Error('Unknown error'));
        setupError = errObj;
        console.warn(`Attempt ${attempt + 1} failed:`, errObj);

        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1500 + (attempt * 1000)));
      }

      if (setupError && !setupResult) {
        throw new Error(setupError.message || 'Account setup timed out. Please try logging in - your account may have been created despite this message.');
      }

      setSuccess(true);

      // Sign out to ensure the user sees the login form on redirected /login page
      await supabase.auth.signOut();

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      console.error('Signup error:', err);
      const errorMessage = err.message || 'Failed to create account';

      // Check for specific database/RLS errors
      if (errorMessage.includes('database') ||
        errorMessage.includes('row-level security') ||
        errorMessage.includes('RLS') ||
        errorMessage.includes('42501') ||
        errorMessage.includes('violates row-level security')) {
        setError('Database error: Unable to save user account. Please ensure the database trigger is configured correctly. Contact support if this persists.');
      } else if (errorMessage.includes('Profile creation blocked') || errorMessage.includes('security policy')) {
        setError('Account creation blocked by security policy. Please contact support with your user ID.');
      } else {
        setError(errorMessage);
      }
    } finally {
      if (!success) {
        setLoading(false);
      }
    }
  };

  if (success) {
    return (
      <div className="ngo-signup-page">
        <div className="form-container">
          <div className="form-section" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h1>Account Created!</h1>
            <p>Your NGO account has been set up successfully.</p>
            <p style={{ color: 'var(--muted)', marginTop: '1rem' }}>Redirecting to login page...</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
              style={{ marginTop: '2rem' }}
            >
              Go to Login Now
            </button>
          </div>
        </div>
      </div>
    );
  }



  if (!invitation && !error) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="ngo-signup-page">
      <div className="form-container">
        <div className="form-section">
          <h1>Set Up Your NGO Account</h1>

          {invitation && (
            <div style={{
              background: 'var(--secondary)',
              padding: '1rem',
              borderRadius: 'var(--radius)',
              marginBottom: '2rem'
            }}>
              {companyName ? (
                <p style={{ margin: 0, fontWeight: 600 }}>Invited by: {companyName}</p>
              ) : (
                <p style={{ margin: 0, fontWeight: 600 }}>You have been invited to join the platform</p>
              )}
              <p style={{ margin: '0.5rem 0 0 0', color: 'var(--muted)', fontSize: '0.9rem' }}>
                Email: {invitation.ngo_email}
              </p>
            </div>
          )}

          {error && (
            <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Minimum 8 characters"
                minLength={8}
              />
              <div className="help-text">
                Password must be at least 8 characters long
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="Re-enter password"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !invitation}>
                {loading ? 'Creating Account...' : 'Create Account & Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NGOSignup;

